import { Button } from '../../../components/ui/button';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../../components/ui/form-dialog';
// apps/web/src/pages/pcp/conferencia-pv/dividir-entre-itens-dialog.tsx — Fase 12, Plano 29 (NFE-05, D-08, D-12).
// "Dividir entre itens…": reparte a quantidade física conferida de UM item da nota entre vários itens de PV.
// A soma precisa igualar a quantidade física; quantidade acima do saldo de uma parte pede justificativa (>= 5).
// Superfície compartilhada de formulário mantém cabeçalho e ações acessíveis nos dois temas.
import { useRef, useState, type ReactElement } from 'react';
import type { CandidatoItemPv } from '@pieltec/contracts';
import {
  divisaoValida,
  excedenteDaAlocacao,
  mensagemExcedente,
  MINIMO_JUSTIFICATIVA,
  precisaJustificativa,
  rotuloOpcaoItemPv,
  type AlocacaoEmEdicao,
} from '../../../lib/vendas/recebimento-pv';

const FORMATO_QUANTIDADE = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 });

interface LinhaEmEdicao {
  readonly pedidoVendaItemId: string;
  readonly quantidade: string;
  readonly justificativa: string;
}

export interface DividirEntreItensDialogProps {
  readonly documentoFiscalItemId: string;
  readonly candidatos: readonly CandidatoItemPv[];
  readonly quantidadeFisica: number;
  /** Divisão já editada (ou sugerida pela API) para reabrir com os valores preenchidos. */
  readonly inicial: readonly AlocacaoEmEdicao[];
  readonly onConfirmar: (alocacoes: AlocacaoEmEdicao[]) => void;
  readonly onCancelar: () => void;
}

function numero(texto: string): number {
  const valor = Number.parseFloat(texto.replace(',', '.'));
  return Number.isFinite(valor) ? valor : 0;
}

export function DividirEntreItensDialog({ documentoFiscalItemId, candidatos, quantidadeFisica, inicial, onConfirmar, onCancelar }: DividirEntreItensDialogProps): ReactElement {
  const [linhas, setLinhas] = useState<LinhaEmEdicao[]>(() =>
    candidatos.map((candidato) => {
      const existente = inicial.find((parte) => parte.pedidoVendaItemId === candidato.pedidoVendaItemId);
      return { pedidoVendaItemId: candidato.pedidoVendaItemId, quantidade: existente ? String(existente.quantidade) : '', justificativa: existente?.justificativa ?? '' };
    }),
  );
  const primeiroCampo = useRef<HTMLInputElement>(null);
  const idTitulo = `dividir-titulo-${documentoFiscalItemId}`;
  const [acionador] = useState(() => document.activeElement instanceof HTMLElement ? document.activeElement : null);

  const partes: AlocacaoEmEdicao[] = linhas.map((linha) => ({
    pedidoVendaItemId: linha.pedidoVendaItemId,
    quantidade: numero(linha.quantidade),
    saldoAReceber: candidatos.find((candidato) => candidato.pedidoVendaItemId === linha.pedidoVendaItemId)?.saldoAReceber ?? Number.POSITIVE_INFINITY,
    justificativa: linha.justificativa,
  }));
  const soma = partes.reduce((acumulado, parte) => acumulado + parte.quantidade, 0);
  const somaFecha = divisaoValida(
    partes.map((parte) => parte.quantidade),
    quantidadeFisica,
  );
  const faltaJustificativa = precisaJustificativa(partes);
  const diferenca = quantidadeFisica - soma;

  const alterar = (indice: number, parcial: Partial<LinhaEmEdicao>) =>
    setLinhas((atuais) => atuais.map((linha, posicao) => (posicao === indice ? { ...linha, ...parcial } : linha)));

  return (
    <FormDialog open onOpenChange={(open) => { if (!open) onCancelar(); }}>
      <FormDialogContent size="wide" aria-labelledby={idTitulo} onOpenAutoFocus={(evento) => { evento.preventDefault(); primeiroCampo.current?.focus(); }} onCloseAutoFocus={(evento) => { if (acionador?.isConnected) { evento.preventDefault(); acionador.focus(); } }}>
        <FormDialogHeader>
          <FormDialogTitle id={idTitulo} className="text-lg font-semibold text-foreground">
            Dividir entre itens
          </FormDialogTitle>
          <FormDialogDescription className="mt-1 text-sm text-muted-foreground">
            Reparta os {FORMATO_QUANTIDADE.format(quantidadeFisica)} conferidos entre os itens de PV abaixo. A soma precisa fechar com a quantidade física.
          </FormDialogDescription>
        </FormDialogHeader>
        <FormDialogBody className="space-y-4">
        <div className="space-y-3">
          {candidatos.map((candidato, indice) => {
            const linha = linhas[indice];
            const parte = partes[indice];
            if (!linha || !parte) return null;
            const excedente = excedenteDaAlocacao(parte);
            const idQuantidade = `dividir-qtd-${documentoFiscalItemId}-${candidato.pedidoVendaItemId}`;
            const idJustificativa = `dividir-just-${documentoFiscalItemId}-${candidato.pedidoVendaItemId}`;
            return (
              <div key={candidato.pedidoVendaItemId} className="space-y-2 rounded-lg border bg-muted/40 p-3 text-sm">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-foreground" title={rotuloOpcaoItemPv(candidato)}>
                      {rotuloOpcaoItemPv(candidato)}
                    </div>
                    <div className="truncate text-muted-foreground">{candidato.descricao}</div>
                  </div>
                  <div className="sm:w-32 shrink-0">
                    <label htmlFor={idQuantidade} className="mb-1 block text-muted-foreground">
                      Quantidade
                    </label>
                    <input
                      id={idQuantidade}
                      ref={indice === 0 ? primeiroCampo : undefined}
                      type="number"
                      min={0}
                      step="any"
                      inputMode="decimal"
                      value={linha.quantidade}
                      onChange={(evento) => alterar(indice, { quantidade: evento.target.value })}
                      className="min-h-11 w-full rounded border bg-background px-3 py-2 text-sm font-mono focus-visible:outline-2 focus-visible:outline-ring"
                    />
                  </div>
                </div>
                {excedente > 0 && (
                  <div className="space-y-2">
                    <div className="rounded border border-amber-200 bg-amber-50 p-3 leading-relaxed text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">{mensagemExcedente(excedente)}</div>
                    <div>
                      <label htmlFor={idJustificativa} className="mb-1 block text-muted-foreground">
                        Justificativa (obrigatória)
                      </label>
                      <textarea
                        id={idJustificativa}
                        rows={2}
                        value={linha.justificativa}
                        onChange={(evento) => alterar(indice, { justificativa: evento.target.value })}
                        aria-invalid={linha.justificativa.trim().length < MINIMO_JUSTIFICATIVA}
                        className="min-h-11 w-full rounded border bg-background px-3 py-2 text-sm focus-visible:outline-2 focus-visible:outline-ring"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={`rounded border p-3 text-sm ${somaFecha ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100' : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100'}`} role="status">
          Soma: <strong>{FORMATO_QUANTIDADE.format(soma)}</strong> de <strong>{FORMATO_QUANTIDADE.format(quantidadeFisica)}</strong>
          {!somaFecha && (
            <span>
              {' '}
              — a soma das quantidades precisa ser igual à quantidade física conferida ({diferenca > 0 ? `faltam ${FORMATO_QUANTIDADE.format(diferenca)}` : `sobram ${FORMATO_QUANTIDADE.format(-diferenca)}`}).
            </span>
          )}
        </div>

        </FormDialogBody>
        <FormDialogFooter>
          <Button type="button" variant="outline" onClick={onCancelar} className="min-h-11">
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => onConfirmar(partes)}
            disabled={!somaFecha || faltaJustificativa}
            className="min-h-11"
          >
            Confirmar divisão
          </Button>
        </FormDialogFooter>
      </FormDialogContent>
    </FormDialog>
  );
}
